import React, { useEffect, useMemo, useState } from "react";
import { adminApi } from "./api";
import { shortDate } from "./format";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, X, Users, LayoutGrid, Table } from "lucide-react";
import ExportMenu from "./ExportMenu";
import CRMKanban from "./CRMKanban";

const STAGES = ["new", "contacted", "qualified", "proposal", "won", "lost"];
const STAGE_CHIP = {
  new: "ax-chip ax-chip-info",
  contacted: "ax-chip",
  qualified: "ax-chip ax-chip-warn",
  proposal: "ax-chip ax-chip-gold",
  won: "ax-chip ax-chip-success",
  lost: "ax-chip ax-chip-danger",
};

const EMPTY = {
  name: "",
  phone: "",
  email: "",
  city: "",
  project_type: "",
  home_size: "",
  budget: "",
  source: "website",
  stage: "new",
  notes: "",
};

export default function CRMModule() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [editing, setEditing] = useState(null); // null | "new" | leadObj
  const [view, setView] = useState("table"); // table | kanban

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/leads");
      setLeads(r.data?.leads || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return leads.filter((l) => {
      if (stageFilter !== "all" && l.stage !== stageFilter) return false;
      if (!term) return true;
      return (
        (l.name || "").toLowerCase().includes(term) ||
        (l.phone || "").toLowerCase().includes(term) ||
        (l.email || "").toLowerCase().includes(term) ||
        (l.city || "").toLowerCase().includes(term)
      );
    });
  }, [leads, q, stageFilter]);

  const onSave = async (data, existing) => {
    try {
      if (existing) {
        await adminApi.put(`/leads/${existing.id}`, data);
        toast.success("Lead updated");
      } else {
        await adminApi.post("/leads", data);
        toast.success("Lead added");
      }
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const onDelete = async (lead) => {
    if (!window.confirm(`Delete lead "${lead.name}"?`)) return;
    try {
      await adminApi.delete(`/leads/${lead.id}`);
      toast.success("Lead deleted");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="p-10 max-w-[1320px] mx-auto" data-testid="crm-module">
      <header className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-3.5 h-3.5" style={{ color: "var(--ax-gold)" }} />
            <span className="text-[10.5px] tracking-[0.32em] uppercase" style={{ color: "var(--ax-gold)" }}>
              CRM
            </span>
          </div>
          <h1 className="ax-serif text-5xl tracking-[-0.02em]">Leads</h1>
          <p className="text-sm mt-3" style={{ color: "var(--ax-text-dim)" }}>
            Capture, qualify and convert your luxury home pipeline.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ExportMenu entity="leads" testidPrefix="crm-export" />
          <button
            type="button"
            className="ax-btn ax-btn-gold"
            onClick={() => setEditing("new")}
            data-testid="crm-add-lead"
          >
            <Plus className="w-4 h-4" /> Add lead
          </button>
        </div>
      </header>

      <div className="ax-glass mb-5 p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg flex-1 min-w-[240px]" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid var(--ax-border)" }}>
          <Search className="w-4 h-4" style={{ color: "var(--ax-text-muted)" }} />
          <input
            className="bg-transparent flex-1 outline-none text-sm"
            placeholder="Search name, phone, email, city"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="crm-search"
          />
        </div>
        <select
          className="ax-select"
          style={{ width: 180 }}
          value={stageFilter}
          onChange={(e) => setStageFilter(e.target.value)}
          data-testid="crm-filter-stage"
        >
          <option value="all">All stages</option>
          {STAGES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <div className="text-xs" style={{ color: "var(--ax-text-muted)" }}>
          {filtered.length} / {leads.length}
        </div>
        <div
          className="flex items-center gap-1 p-1 rounded-lg"
          style={{ background: "rgba(255,255,255,0.03)", border: "1px solid var(--ax-border)" }}
          data-testid="crm-view-toggle"
        >
          <button
            type="button"
            className="ax-btn ax-btn-ghost px-2 py-1 text-[11px]"
            style={view === "table" ? { background: "rgba(212,175,55,0.14)", color: "var(--ax-gold)" } : undefined}
            onClick={() => setView("table")}
            data-testid="crm-view-table"
          >
            <Table className="w-3.5 h-3.5" /> Table
          </button>
          <button
            type="button"
            className="ax-btn ax-btn-ghost px-2 py-1 text-[11px]"
            style={view === "kanban" ? { background: "rgba(212,175,55,0.14)", color: "var(--ax-gold)" } : undefined}
            onClick={() => setView("kanban")}
            data-testid="crm-view-kanban"
          >
            <LayoutGrid className="w-3.5 h-3.5" /> Kanban
          </button>
        </div>
      </div>

      {view === "kanban" && (
        <CRMKanban leads={filtered} onEdit={(l) => setEditing(l)} onChanged={refresh} />
      )}

      {view === "table" && (
      <div className="ax-glass overflow-hidden" data-testid="crm-table-wrap">
        <div className="ax-scroll overflow-x-auto">
          <table className="ax-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Contact</th>
                <th>City</th>
                <th>Project</th>
                <th>Budget</th>
                <th>Stage</th>
                <th>Created</th>
                <th style={{ width: 110 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8} className="text-center py-10 text-sm" style={{ color: "var(--ax-text-muted)" }}>
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-10 text-sm" style={{ color: "var(--ax-text-muted)" }}>
                    No leads match.
                  </td>
                </tr>
              )}
              {!loading &&
                filtered.map((l) => (
                  <tr key={l.id} data-testid={`crm-row-${l.id}`}>
                    <td>
                      <div className="font-medium text-sm">{l.name}</div>
                      {l.source && (
                        <div className="text-[11px] mt-0.5" style={{ color: "var(--ax-text-muted)" }}>
                          via {l.source}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="text-xs ax-mono">{l.phone || "—"}</div>
                      <div className="text-[11px]" style={{ color: "var(--ax-text-muted)" }}>
                        {l.email || ""}
                      </div>
                    </td>
                    <td className="text-sm">{l.city || "—"}</td>
                    <td className="text-sm">
                      {l.project_type || "—"}
                      {l.home_size ? <span className="text-[11px]" style={{ color: "var(--ax-text-muted)" }}> · {l.home_size}</span> : null}
                    </td>
                    <td className="text-sm ax-mono">{l.budget || "—"}</td>
                    <td>
                      <span className={STAGE_CHIP[l.stage] || "ax-chip"}>{l.stage || "new"}</span>
                    </td>
                    <td className="text-xs" style={{ color: "var(--ax-text-muted)" }}>
                      {shortDate(l.created_at)}
                    </td>
                    <td>
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          type="button"
                          className="ax-btn ax-btn-ghost px-2"
                          onClick={() => setEditing(l)}
                          data-testid={`crm-edit-${l.id}`}
                          title="Edit"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          className="ax-btn ax-btn-ghost ax-btn-danger px-2"
                          onClick={() => onDelete(l)}
                          data-testid={`crm-delete-${l.id}`}
                          title="Delete"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {editing && (
        <LeadDialog
          lead={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSave={onSave}
        />
      )}
    </div>
  );
}

function LeadDialog({ lead, onClose, onSave }) {
  const [form, setForm] = useState(() => ({ ...EMPTY, ...(lead || {}) }));
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    await onSave(form, lead);
    setSaving(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)" }}
      data-testid="crm-dialog"
    >
      <form
        onSubmit={submit}
        className="ax-glass w-full max-w-2xl p-7 relative"
        style={{ borderRadius: 18 }}
      >
        <button
          type="button"
          className="absolute top-4 right-4 text-white/60 hover:text-white"
          onClick={onClose}
          data-testid="crm-dialog-close"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="mb-5">
          <div className="text-[10.5px] tracking-[0.32em] uppercase" style={{ color: "var(--ax-gold)" }}>
            {lead ? "Edit" : "New"} lead
          </div>
          <h2 className="ax-serif text-3xl mt-1">{lead ? lead.name : "Capture a new lead"}</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="ax-label">Name *</label>
            <input className="ax-input" value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="crm-field-name" />
          </div>
          <div>
            <label className="ax-label">Phone</label>
            <input className="ax-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="crm-field-phone" />
          </div>
          <div>
            <label className="ax-label">Email</label>
            <input className="ax-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="crm-field-email" />
          </div>
          <div>
            <label className="ax-label">City</label>
            <input className="ax-input" value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="crm-field-city" />
          </div>
          <div>
            <label className="ax-label">Project type</label>
            <input className="ax-input" placeholder="Villa, Apartment, Penthouse…" value={form.project_type} onChange={(e) => set("project_type", e.target.value)} data-testid="crm-field-project" />
          </div>
          <div>
            <label className="ax-label">Home size</label>
            <input className="ax-input" placeholder="4 BHK, 6500 sqft…" value={form.home_size} onChange={(e) => set("home_size", e.target.value)} data-testid="crm-field-size" />
          </div>
          <div>
            <label className="ax-label">Budget (₹)</label>
            <input className="ax-input" value={form.budget} onChange={(e) => set("budget", e.target.value)} data-testid="crm-field-budget" />
          </div>
          <div>
            <label className="ax-label">Source</label>
            <select className="ax-select" value={form.source || ""} onChange={(e) => set("source", e.target.value)} data-testid="crm-field-source">
              <option value="website">Website</option>
              <option value="referral">Referral</option>
              <option value="walk-in">Walk-in</option>
              <option value="ad">Ad</option>
              <option value="event">Event</option>
            </select>
          </div>
          <div>
            <label className="ax-label">Stage</label>
            <select className="ax-select" value={form.stage} onChange={(e) => set("stage", e.target.value)} data-testid="crm-field-stage">
              {STAGES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="ax-label">Notes</label>
            <textarea className="ax-textarea" rows={3} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} data-testid="crm-field-notes" />
          </div>
        </div>

        <div className="mt-7 flex items-center justify-end gap-3">
          <button type="button" className="ax-btn ax-btn-ghost" onClick={onClose} data-testid="crm-dialog-cancel">
            Cancel
          </button>
          <button type="submit" className="ax-btn ax-btn-gold" disabled={saving} data-testid="crm-dialog-save">
            {saving ? "Saving…" : lead ? "Save changes" : "Add lead"}
          </button>
        </div>
      </form>
    </div>
  );
}
